import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { EmailMonitor, EmailStatusBadge } from "@/components/admin/EmailMonitor";
import { LeadDetailModal } from "@/components/admin/LeadDetailModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_OPTIONS = ["new", "reviewed", "contacted", "closed"];

export default function Admin() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem("sue.adminKey") || "");
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState([]);
  const [monitoring, setMonitoring] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async (key) => {
    setLoading(true);
    setError("");
    try {
      const [leadsResponse, monitoringResponse] = await Promise.all([
        axios.get(`${API}/quote-requests`, { params: { admin_key: key } }),
        axios.get(`${API}/email-monitoring`, { params: { admin_key: key } }),
      ]);
      setItems(leadsResponse.data);
      setMonitoring(monitoringResponse.data);
      setAuthed(true);
      localStorage.setItem("sue.adminKey", key);
    } catch (requestError) {
      setAuthed(false);
      setError("Clave de administración inválida");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const storedKey = localStorage.getItem("sue.adminKey");
    if (storedKey) load(storedKey);
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      const response = await axios.patch(`${API}/quote-requests/${id}`, { status }, { params: { admin_key: adminKey } });
      setItems((previous) => previous.map((item) => (item.id === id ? response.data : item)));
      setActive((current) => (current?.id === id ? response.data : current));
      toast.success("Estado actualizado");
    } catch (requestError) {
      toast.error("No se pudo actualizar el estado");
    }
  };

  const retryEmail = async (id) => {
    setRetrying(true);
    try {
      const response = await axios.post(`${API}/quote-requests/${id}/retry-email`, null, { params: { admin_key: adminKey } });
      setItems((previous) => previous.map((item) => (item.id === id ? response.data : item)));
      setActive(response.data);
      await load(adminKey);
      setActive(response.data);
      toast.success(response.data.email_notification_status === "sent" ? "Notificación enviada" : "El reintento falló");
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || "No se pudo reintentar el envío");
    } finally {
      setRetrying(false);
    }
  };

  const filtered = filter === "all" ? items : items.filter((item) => item.status === filter);

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black px-6 text-white">
        <form onSubmit={(event) => { event.preventDefault(); load(adminKey); }} className="luxury-glass w-full max-w-sm p-10" data-testid="admin-login-form">
          <h1 className="font-display text-2xl" data-testid="admin-login-title">Sue Production · Admin</h1>
          <p className="mb-6 mt-2 text-sm text-white/60">Ingresa tu clave para ver los leads.</p>
          <input type="password" placeholder="Clave de administración" value={adminKey} onChange={(event) => setAdminKey(event.target.value)} className="luxury-input mb-6" data-testid="admin-key-input" />
          {error && <div className="mb-4 text-xs text-red-400" data-testid="admin-login-error">{error}</div>}
          <button type="submit" disabled={loading} className="w-full bg-white py-3 text-xs uppercase tracking-[0.22em] text-black disabled:opacity-50" data-testid="admin-login-submit-button">
            {loading ? "Cargando…" : "Entrar"}
          </button>
          <a href="/" className="mt-6 block text-center text-xs uppercase tracking-[0.22em] text-white/40 hover:text-white" data-testid="admin-back-to-site-link">← Volver al sitio</a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="flex items-center justify-between border-b border-white/10 px-6 py-5 md:px-12">
        <div className="font-display text-lg" data-testid="admin-page-title">Sue · Production — Leads</div>
        <a href="/" className="text-xs uppercase tracking-[0.22em] text-white/60 hover:text-white" data-testid="admin-site-link">← Sitio</a>
      </header>

      <EmailMonitor monitoring={monitoring} />

      <nav className="flex flex-wrap items-center gap-2 px-6 py-8 md:px-12" aria-label="Filtros de leads">
        {["all", ...STATUS_OPTIONS].map((status) => (
          <button type="button" key={status} onClick={() => setFilter(status)} className={`border px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] transition-colors ${filter === status ? "border-white bg-white text-black" : "border-white/15 text-white/60 hover:text-white"}`} data-testid={`admin-filter-${status}-button`}>
            {status} ({status === "all" ? items.length : items.filter((item) => item.status === status).length})
          </button>
        ))}
        <button type="button" onClick={() => load(adminKey)} disabled={loading} className="ml-auto inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-white disabled:opacity-40" data-testid="admin-refresh-button">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} aria-hidden="true" /> Actualizar
        </button>
      </nav>

      <main className="px-6 pb-20 md:px-12">
        <div className="luxury-glass overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm" data-testid="admin-leads-table">
            <thead className="text-[10px] uppercase tracking-[0.22em] text-white/40">
              <tr className="border-b border-white/10"><th className="p-4 text-left">Nombre</th><th className="p-4 text-left">Email</th><th className="p-4 text-left">Servicio</th><th className="p-4 text-left">Presupuesto</th><th className="p-4 text-left">Lead</th><th className="p-4 text-left">Notificación</th><th className="p-4 text-left">Creado</th></tr>
            </thead>
            <tbody>
              {filtered.map((lead) => (
                <tr key={lead.id} className="border-b border-white/5 transition-colors hover:bg-white/[0.03]" data-testid={`lead-row-${lead.id}`}>
                  <td className="p-4"><button type="button" onClick={() => setActive(lead)} className="text-left hover:text-lux" data-testid={`lead-open-${lead.id}-button`}>{lead.name}{lead.business_name && <span className="block text-xs text-white/40">{lead.business_name}</span>}</button></td>
                  <td className="p-4 text-white/70" data-testid={`lead-email-${lead.id}`}>{lead.email}</td>
                  <td className="p-4 text-white/70" data-testid={`lead-service-${lead.id}`}>{lead.service_type}</td>
                  <td className="p-4 text-white/70" data-testid={`lead-budget-${lead.id}`}>{lead.budget_range}</td>
                  <td className="p-4"><span className="border border-white/10 px-2 py-1 text-[10px] uppercase tracking-[0.18em]" data-testid={`lead-status-${lead.id}`}>{lead.status}</span></td>
                  <td className="p-4"><EmailStatusBadge status={lead.email_notification_status} testId={`lead-email-status-${lead.id}`} /></td>
                  <td className="p-4 text-xs text-white/50" data-testid={`lead-created-${lead.id}`}>{new Date(lead.created_at).toLocaleString()}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-white/40" data-testid="admin-empty-state">No hay leads.</td></tr>}
            </tbody>
          </table>
        </div>
      </main>

      <LeadDetailModal lead={active} onClose={() => setActive(null)} onStatus={setStatus} onRetry={retryEmail} retrying={retrying} monitoringEnabled={monitoring?.enabled} />
    </div>
  );
}